import { HttpErrorResponse, HttpEvent, HttpHandler, HttpRequest, HttpResponse } from '@angular/common/http';
import { DestroyRef, inject, Injectable, Injector } from '@angular/core';
import { BUILD_VERSION, CustomEvents, GUEST_LOGIN_WITH_GOOGLE_CODE, StorageHelper, Utils, YAML_ERROR_FLAG } from '@app/_helpers';
import { BROKER_NOT_AVAILABLE, BROKER_NOT_AVAILABLE_LEARN_MORE, OFFLINE_MESSAGE, SERVICE_UNAVAILABLE_ERROR_CODE, STG_LOOP_DETECTED } from '@app/_shared/strings';
import { BehaviorSubject, from, Observable, Subject, throwError } from 'rxjs';
import { catchError, filter, mergeMap, switchMap, take } from 'rxjs/operators';
import { WebErrorCodes } from '../../../../utProto/webEnums_pb';
import { AuthService, MultiBrokerRefreshTokenResponse } from '../../../modules/auth';
import * as CryptoJS from 'crypto-js';
import { ConfigurationService, EventsService, NgxToastrService } from '../../providers';
import { MixpanelService } from '../mixpanel/mixpanel.service';
import { ApiErrorTypes, MIXPANEL_EVENTS } from '../mixpanel/mixpanel.events';

@Injectable({
    providedIn: 'root'
})
export class HttpInterceptorService {
    private refreshTokenInProgress = false;
    private refreshTokenSubject: Subject<string> = new BehaviorSubject<string>(null);
    authService: AuthService;
    toastrId: number;
    private isOnline: boolean = navigator.onLine

    // These error messages are critical and need to be shown as red toasts.
    criticalErrorMessages = new Set([STG_LOOP_DETECTED]);

    constructor(
        private injector: Injector,
        private toastr: NgxToastrService,
        private configurationService: ConfigurationService,
        private mixpanel: MixpanelService,
        private eventsService: EventsService
    ) {
        window.addEventListener('online',  () => this.isOnline = true);
        window.addEventListener('offline',  () => this.isOnline = false);
    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        this.authService = this.injector.get(AuthService);

        // Do not check token if this is a refresh or local asset (Ex :- config file) request.
        if (this.isRefreshRequest(request) || !request.url.startsWith('http')) {
            return this.handleRequest(request, next);
        }

        if (this.authService.isAuthenticated && request.url != this.configurationService.applicationUrls.geolocationAPIUrl) {
            const accessExpired = this.authService.accessTokenObj.isExpired;
            const refreshExpired = this.authService.refreshTokenObj.isExpired;

            if (accessExpired && refreshExpired && !this.isLogoutRequest(request)) {
                this.authService.logoutApi();
                return;
            }

            if (accessExpired && !refreshExpired) {
                if (!this.refreshTokenInProgress) {
                    this.refreshTokenInProgress = true;
                    this.refreshTokenSubject.next(null);
                    return this.authService.refreshToken().pipe(
                        switchMap((authResponse: MultiBrokerRefreshTokenResponse) => {
                            this.refreshTokenInProgress = false;
                            this.refreshTokenSubject.next(authResponse.access_token);
                            return this.handleRequest(this.addToken(request, authResponse.access_token), next);
                        }),
                        catchError((error) => {
                            // If refresh token fails, logout the user
                            this.refreshTokenInProgress = false;
                            this.authService.logoutApi();
                            return throwError(error);
                        })
                    );
                } else {
                    return this.refreshTokenSubject.pipe(
                        filter(result => result !== null),
                        take(1),
                        switchMap((res) => {
                            return this.handleRequest(this.addToken(request, res), next)
                        })
                    );
                }
            }

            // Access token is valid, proceed with request
            if (!accessExpired) {
                return this.handleRequest(
                    this.addToken(request, this.authService.userInfo.access),
                    next
                );
            }
        }

        return this.handleRequest(request, next);

    }

    private handleRequest(request: HttpRequest<any>, next: HttpHandler) {
        return from(this.modifyRequest(request)).pipe(
            switchMap(modifiedRequest => next.handle(modifiedRequest)),
            mergeMap((response: HttpEvent<any>) => this.successHandler(response)),
            this.errorHandler
        ) as Observable<HttpEvent<any>>;
    }

    private generateRequestSignature(method: string, path: string, body: string = ''): { signature: string; timestamp: string } {
        const timestamp = Math.floor(Date.now() / 1000).toString();
        const secretKey = this.configurationService.remoteConfigCacheObj?.AI_RECOMMENDATION_CONFIG?.secret_key;
        const message = `${timestamp}${method}${path}${body}`;
        const signature = CryptoJS.HmacSHA256(message, secretKey).toString();
        return { signature, timestamp };
    }

    /**
     * Modifies the HTTP request by encrypting the request body if encryption is enabled.
     * @param request - The original HTTP request.
     * @returns A promise that resolves to the modified HTTP request.
     */
    private async modifyRequest(request: HttpRequest<any>): Promise<HttpRequest<any>> {
        if (BUILD_VERSION !== '==BUILD_VERSION_PLACEHOLDER==' && BUILD_VERSION !== '') {
            request = request.clone({
                setHeaders: { 'Version': `web-${BUILD_VERSION}` }
            });
        }
        const isEncryptionEnabled = this.configurationService.remoteConfigCacheObj?.SECURITY_USE_ENCRYPTION || false;

        if (isEncryptionEnabled && request.body && request.url.includes(this.configurationService.applicationUrls?.aiAssistantServer)) {
            const encryptionKey = this.configurationService.remoteConfigCacheObj?.SECURITY_ENCRYPTION_KEY;
            const secretKey = this.configurationService.remoteConfigCacheObj?.SECURITY_SECRET_KEY;

            const encryptedBody = await Utils.AesGcmEncryption.encryptRequestBody(JSON.stringify(request.body), encryptionKey, secretKey);
            return request.clone({
                body: encryptedBody,
                setHeaders: {
                    'X-Encryption-Enabled': 'true'
                }
            });
        } else if (request.body && request.url.includes(this.configurationService.applicationUrls?.aiRecommendationServer)) {
            const apiKey = this.configurationService.remoteConfigCacheObj?.AI_RECOMMENDATION_CONFIG?.api_key;
            const method = request.method;
            const path = new URL(request.url).pathname;
            const bodyStr = request.body ? JSON.stringify(request.body) : '';
            const { signature, timestamp } = this.generateRequestSignature(method, path, bodyStr);

            return request.clone({
                setHeaders: {
                    'X-API-Key': apiKey,
                    'X-Signature': signature,
                    'X-Timestamp': timestamp,
                }
            });
        } else if (request.url.includes(this.configurationService.applicationUrls?.analyticsServer)) {
            const apiKey = this.configurationService.remoteConfigCacheObj?.ANALYTICS_SERVER_CONFIG?.api_key;
            const method = request.method;
            const path = new URL(request.url).pathname;
            const bodyStr = request.body ? JSON.stringify(request.body) : '';
            const { signature, timestamp } = this.generateRequestSignature(method, path, bodyStr);

            request = request.clone({
                setHeaders: {
                    'X-API-Key': apiKey,
                    'X-Signature': signature,
                    'X-Timestamp': timestamp,
                }
            });
        }

        return request;
    }

    /**
     * Handles the success response from the HTTP request.
     * If encryption is enabled and the response is from the AI assistant server,
     * it decrypts the response body using the encryption key and secret key.
     * @param response The HTTP event response.
     * @returns An observable of the modified HTTP event response.
     */
    private successHandler(response: HttpEvent<any>): Observable<HttpEvent<any>> {
        const isEncryptionEnabled = this.configurationService.remoteConfigCacheObj?.SECURITY_USE_ENCRYPTION || false;

        if (isEncryptionEnabled && response instanceof HttpResponse && response.url.includes(this.configurationService.applicationUrls.aiAssistantServer)) {
            const encryptionKey = this.configurationService.remoteConfigCacheObj?.SECURITY_ENCRYPTION_KEY;
            const secretKey = this.configurationService.remoteConfigCacheObj?.SECURITY_SECRET_KEY;

            return from(Utils.AesGcmEncryption.decryptRequestBody(response.body.data, encryptionKey, secretKey).then(stringResponse => {
                const parsedResponse = JSON.parse(stringResponse);
                return response.clone({
                    body: parsedResponse
                });
            }));
        }

        return from(Promise.resolve(response));
    }


    private addToken(request: HttpRequest<any>, token: string) {
        const newHeaders = {};

        // newHeaders['Authorization'] = `BREARER ${token}`; //TODO: Handle Token with Bearer string
        if (this.authService.isMasterTrustLogin && request.url.includes('/ugateway/')) {
            StorageHelper.MasterTrustAccessToken ? newHeaders['Authorization'] = `Bearer ${StorageHelper.MasterTrustAccessToken}` : this.authService.logoutApi();
        } else if (StorageHelper.operatorAdminToken && request.url.startsWith(this.configurationService.applicationUrls.operatorDashboardHost + 'admin/')) {
            newHeaders['Authorization'] = StorageHelper.operatorAdminToken;
        } else if (StorageHelper.operatorToken && request.url.startsWith(this.configurationService.applicationUrls.operatorDashboardHost)) {
            newHeaders['Authorization'] = StorageHelper.operatorToken;
        } else {
            newHeaders['Authorization'] = token;
        }


        return request.clone({
            setHeaders: newHeaders
        });
    }

    errorHandler = catchError((error, _) => {
        if (!this.isOnline) {
            this.toastrId = this.toastr.simpleCustomToaster(OFFLINE_MESSAGE);
            try {
                this.mixpanel.track(MIXPANEL_EVENTS.API_ERROR, {
                    url: Utils.sanitizeUrl(error?.url),
                    status_code: error?.status ?? undefined,
                    type: ApiErrorTypes.OFFLINE,
                    internet_connected: false,
                    status_text: "User is offline",
                });
            } catch (e) {
                console.error(e);
            }
            return throwError(error);
        }
    
        this.handleServerError(error);
        return throwError(error);
    });

    handleServerError(error: HttpErrorResponse) {
        const err = error.error;
    
        if (error.url === this.configurationService.applicationUrls.geolocationAPIUrl) {
            this.toastrId = this.toastr.simpleCustomToaster(OFFLINE_MESSAGE);
            return;
        }
    
        // Browser-level failure — request never reached server, no point calling ipify
        if (err instanceof ProgressEvent || err instanceof ErrorEvent) {
            this.trackApiError(error, "Browser failed to send request", this.isOnline);
            return;
        }
    
        this.authService.getPublicIPAddress().subscribe({
            next: () => {
                let errorMsg = null;
    
                if (error.status === 0) {
                    errorMsg = 'Server is disconnected. Please try again after sometime.';
                } else if (error.status === 401) {
                    errorMsg = 'Logged Out!';
                    !this.authService.refreshTokenObj.isExpired && this.toastr.simpleCustomToaster(errorMsg);
                    this.authService.clearLogout();
                    this.trackApiError(error, errorMsg, this.isOnline);
                    return;
                } else if (error.status === 402) {
                    this.eventsService.publish(CustomEvents.CREDITS_EXHAUSTED);
                    this.trackApiError(error, "Plans Limit Breached", this.isOnline);
                    return;
                } else if (error.status === 500) {
                    errorMsg = err?.message ?? err?.error ?? 'Server Error! Something\'s wrong.';
                } else if (error.status === 502) {
                    errorMsg = 'Server is disconnected. Please try again after sometime.';
                } else {
                    if (err && (typeof err.msg === 'string' || typeof err.message === 'string' || typeof err.error === 'string')) {
                        errorMsg = err.msg || err.message || err.error;
                    } else if (err?.hasOwnProperty('error') && err.error.length) {
                        errorMsg = err.error[0];
                    } else if (typeof err === 'object') {
                        const first = err[Object.keys(err)[0]];
                        errorMsg = typeof first === 'string' ? first : first?.[0] ?? null;
                    }
                }
    
                if (errorMsg) {
                    const silentUrls = [
                        this.configurationService.applicationUrls.getConversations,
                        this.configurationService.applicationUrls.getConversationByID
                    ];
                    if (silentUrls.some(url => url && error.url?.startsWith(url))) {
                        this.trackApiError(error, errorMsg, this.isOnline);
                        return;
                    }
    
                    const yamlErrorUrls = [
                        this.configurationService.applicationUrls.validateYml,
                        this.configurationService.applicationUrls.saveYmlPortfolio
                    ];
                    // UT-8139: Backend planned downtime — show one warning toast instead of flooding generic errors.
                    if (err?.code === SERVICE_UNAVAILABLE_ERROR_CODE) {
                        this.toastr.serviceUnavailableToaster(errorMsg);
                    } else if (err?.error_code === BROKER_NOT_AVAILABLE) {
                        if(this.configurationService.remoteConfigCacheObj?.BROKER_NOT_AVAILABLE_HELP_LINK) {
                            this.toastr.clickableCustomToaster(errorMsg, BROKER_NOT_AVAILABLE_LEARN_MORE, this.configurationService.remoteConfigCacheObj?.BROKER_NOT_AVAILABLE_HELP_LINK);
                        } else {
                            this.toastr.clickableCustomToaster(errorMsg);
                        }
                    } else if (!yamlErrorUrls.includes(error.url) && err?.errorCode !== WebErrorCodes.WEBERRORCODES_USERLOCKED) {
                        this.toastrId = this.criticalErrorMessages.has(errorMsg)
                            ? this.toastr.clickableCustomToaster(errorMsg)
                            : this.toastr.simpleCustomToaster(errorMsg, false);
                    }
                }
    
                if (err?.error_type === GUEST_LOGIN_WITH_GOOGLE_CODE) {
                    this.authService.forgotPasswordForEmail();
                }
    
                this.trackApiError(error, errorMsg ?? error.message ?? 'Unknown error', this.isOnline);
            },
            error: (_ipifyError) => {
                this.trackApiError(error, 'Internet connection is not stable.', false);
            }
        });
    }

    private trackApiError(
        error: HttpErrorResponse,
        resolvedErrorMsg: string,
        isOnline: boolean = this.isOnline
    ): void {
        try {
            const url = error?.url;
            if (!url || !url.startsWith('https://')) return;
            if (url.includes('api.ipify.org')) return;
    
            const err = error?.error;
            const status = error.status ?? 0;
            const isBrowserError = err instanceof ProgressEvent || err instanceof ErrorEvent;
            const errorType = this.getErrorType(error, err, status);
    
            const payload: Record<string, unknown> = {
                status_code: status,
                status_text: isBrowserError ? "Browser failed to send request" : error.statusText ?? undefined,
                url: Utils.sanitizeUrl(error.url),
                exception: error.message ?? undefined,
                error_text: resolvedErrorMsg,
                type: errorType,
                internet_connected: isOnline,
            };
    
            if (!isBrowserError && err != null && typeof err === 'object') {
                if (typeof err.message === 'string') payload['message'] = err.message;
                if (typeof err.error === 'string') payload['error'] = err.error;
                if (typeof err.error_code === 'number') payload['error_code'] = err.errorCode;
            }
    
            if (status === 401) {
                payload['refresh_token_expired'] = this.authService.refreshTokenObj?.isExpired ?? null;
            }
    
            this.mixpanel.track(MIXPANEL_EVENTS.API_ERROR, payload);
        } catch (e) {
            console.error('Error tracking API:', e);
        }
    }

    private getErrorType(error: HttpErrorResponse, err: any, status: number): string {
        if (err?.errorCode === WebErrorCodes.WEBERRORCODES_USERLOCKED) {
            return ApiErrorTypes.USER_LOCKED;
        }
        if (status === 0) {
            const isAborted = error?.message?.toLowerCase().includes('abort') || (error?.error?.name === 'AbortError');
            return isAborted ? ApiErrorTypes.CONNECTION_ABORTED : ApiErrorTypes.CONNECTIVITY_ERROR;
        }
        return ApiErrorTypes.getFromStatus(status);
    }

    isRefreshRequest(request: HttpRequest<any>) {
        // For refreshing token. POST request is made on refresh-token API.
        let path = request.url;

        if (request.url.startsWith('http')) {
            // Ignore Domain, only use path
            path = new URL(request.url).pathname
        }

        return path.includes('refresh-token') && (request.method === 'POST')
    }

    isLogoutRequest(request: HttpRequest<any>) {
        return request.url.includes('logout') && (request.method === 'DELETE')
    }

}
